import type {ReactElement} from 'react';
import {useContext} from 'react';
import {Button, IconPlacement, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2/icon';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Text, TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import {useAppDispatch} from '@/hooks';
import {EmailMakerNavLogo} from '../email-maker-nav-logo/email-maker-nav-logo';
import styles from './email-maker-version-history-nav.module.scss';

export function EmailMakerVersionHistoryNav(): ReactElement {
  const {state, actions} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const {editorViewport} = state;

  function exitVersionHistory(): void {
    actions.closeRightPanelView();
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_VERSION_HISTORY_SHEET));
  }

  function renderLeftOptions(): ReactElement {
    return (
      <div className={styles.left}>
        <Icon
          ariaLabel={window.i18next.t('pmwjs_back')}
          className={styles.backButton}
          icon="icon-arrow-left"
          isUsedAsButton
          onClick={exitVersionHistory}
          shape={IconShape.SQUARE}
          size={IconSize.SIZE_ICON_20}
          type={IconType.DARK_GHOST}
        />
        <Text val={window.i18next.t('pmwjs_version_history')} size={TextSize.LG} bold className={styles.title} />
      </div>
    );
  }

  function renderRightOptions(): ReactElement {
    return (
      <div className={styles.right}>
        <Button
          icon="icon-sign-in"
          iconPlacement={IconPlacement.LEFT}
          onClick={exitVersionHistory}
          size={ButtonSize.MEDIUM}
          text={window.i18next.t('pmwjs_exit_version_history')}
          type={ButtonType.SECONDARY} onDark
        />
      </div>
    );
  }

  function renderMobileContent(): ReactElement {
    return (
      <div className={styles.left}>
        <EmailMakerNavLogo editorViewport={editorViewport} />
      </div>
    );
  }

  function renderDesktopContent(): ReactElement {
    return (
      <>
        {renderLeftOptions()}
        {renderRightOptions()}
      </>
    );
  }

  function renderContent(): ReactElement {
    if (editorViewport === EditorViewport.MOBILE) {
      return renderMobileContent();
    }
    return renderDesktopContent();
  }

  return (
    <header className={styles.navWrapper}>
      <nav className={styles.navbar} aria-label={window.i18next.t('pmwjs_email_maker_nav_aria')}>
        {renderContent()}
      </nav>
    </header>
  );
}
